/**
 * Date-of-birth helpers for the Profile modal (RidgeWill/auth#672).
 *
 * Pure, so the tests can pin "today" and so the readback, the confirm step and
 * the section's summary all say a date the same way.
 *
 * The server is the authority on every rule (the 24-hour window, Family Group
 * roles, under 13). Ages here are only for what the member reads, and for
 * deciding whether to ask "are you sure" before an adult becomes under 18.
 */

export interface BirthDateValue {
  month: string;
  day: string;
  year: string;
}

export const EMPTY_BIRTH_DATE: BirthDateValue = { month: '', day: '', year: '' };

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

export const MONTH_OPTIONS = MONTHS.map((label, i) => ({ value: String(i + 1), label }));

/** How far back the Year list goes. */
const OLDEST_AGE = 120;

/**
 * Days 1 to the last day of the chosen month (Jon, 2026-10-30: every part is
 * picked from a list, so nothing can be mistyped). With no month yet, 31; with
 * February and no year yet, 29.
 */
export function dayOptions(month: string, year: string): { value: string; label: string }[] {
  const m = Number(month);
  const y = /^\d{4}$/.test(year) ? Number(year) : 2000; // 2000 is a leap year
  const last = m ? new Date(Date.UTC(y, m, 0)).getUTCDate() : 31;
  return Array.from({ length: last }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }));
}

/** This year back to 120 years ago, newest first. */
export function yearOptions(today: Date = new Date()): { value: string; label: string }[] {
  const top = today.getFullYear();
  return Array.from({ length: OLDEST_AGE + 1 }, (_, i) => ({ value: String(top - i), label: String(top - i) }));
}

/** Clears a day the new month or year does not have (February 31). */
export function fitDay(v: BirthDateValue): BirthDateValue {
  return Number(v.day) > dayOptions(v.month, v.year).length ? { ...v, day: '' } : v;
}

/** The topic support routes date-of-birth corrections by (auth#672). */
export const BIRTH_DATE_TOPIC = 'Correct my date of birth';

/** Where the refusal's "Contact support" goes: the form, topic already chosen. */
export const BIRTH_DATE_SUPPORT_PATH = `/support?topic=${encodeURIComponent(BIRTH_DATE_TOPIC)}`;

/** `YYYY-MM-DD` when all three parts are filled in, else null. Same as the login app's. */
export function toIsoDate(v: BirthDateValue): string | null {
  const m = Number(v.month);
  const d = Number(v.day);
  const y = Number(v.year);
  if (!m || !d || !/^\d{4}$/.test(v.year.trim())) return null;
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** The three fields for a stored date, so Change starts from what is on file. */
export function fromIsoDate(iso: string | null | undefined): BirthDateValue {
  const parts = parseIso(iso);
  if (!parts) return EMPTY_BIRTH_DATE;
  return { month: String(parts.m), day: String(parts.d), year: String(parts.y) };
}

function parseIso(iso: string | null | undefined): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '');
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  // February 30 is not a date. A Date built from it rolls over into March.
  const probe = new Date(Date.UTC(y, m - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== m - 1 || probe.getUTCDate() !== d) return null;
  return { y, m, d };
}

/** "March 4, 2009", or null for something that is not a real date. */
export function formatBirthDate(iso: string | null | undefined): string | null {
  const p = parseIso(iso);
  return p ? `${MONTHS[p.m - 1]} ${p.d}, ${p.y}` : null;
}

/** Whole years old on `today` (local calendar day). Null for an impossible date. */
export function ageOn(iso: string, today: Date = new Date()): number | null {
  const p = parseIso(iso);
  if (!p) return null;
  const ty = today.getFullYear();
  const tm = today.getMonth() + 1;
  const td = today.getDate();
  let age = ty - p.y;
  if (tm < p.m || (tm === p.m && td < p.d)) age -= 1;
  return age;
}

/**
 * What the readback under the fields says, once the date is complete.
 *
 * "March 4, 2009. That makes you 17." is the point of it: a typo in the year
 * is easy to miss in three boxes and hard to miss as a sentence about your age.
 * The modal shows it only on the step that asks an adult to confirm becoming
 * under 18 (Jon, 2026-10-30).
 */
export type Readback =
  | { kind: 'incomplete' }
  | { kind: 'invalid'; text: string }
  | { kind: 'ok'; iso: string; age: number; text: string };

export function readback(v: BirthDateValue, today: Date = new Date()): Readback {
  const iso = toIsoDate(v);
  if (!iso) return { kind: 'incomplete' };
  const formatted = formatBirthDate(iso);
  const age = ageOn(iso, today);
  if (!formatted || age === null) return { kind: 'invalid', text: 'That is not a real date. Check the day and month.' };
  if (age < 0) {
    return { kind: 'invalid', text: 'That date is in the future. Check the year.' };
  }
  return { kind: 'ok', iso, age, text: `${formatted}. That makes you ${age}.` };
}

/**
 * Is this change one from adult to under 18, which asks to confirm first?
 *
 * Judged from the band auth sent when it has one, and from the stored date when
 * it does not. A first entry (nothing on file) never asks: there is no adult
 * to stop being.
 */
export function isAdultToMinor(
  current: { dateOfBirth: string | null; ageBand: string | null },
  nextAge: number,
  today: Date = new Date(),
): boolean {
  if (nextAge >= 18) return false;
  if (current.ageBand) return current.ageBand === '18+';
  if (!current.dateOfBirth) return false;
  const age = ageOn(current.dateOfBirth, today);
  return age !== null && age >= 18;
}
